/*
 * VEEZE reviews — Google reviews rendered by the theme
 * ====================================================
 *
 * The RepOcean app exposes every review it has collected as JSON on a Shopify
 * app proxy (`/apps/repocean-reviews`): author, rating, text, timestamp, photo,
 * plus the listing's real `average_rating` and `user_ratings_total`. That is the
 * same endpoint the app's own carousel reads.
 *
 * So the theme takes the data and leaves the widget alone. The cards below are
 * built from the section's own markup and styled by the section's own CSS,
 * which is the whole point:
 *
 *   - pixel-perfect by construction. No overriding a stranger's minified
 *     stylesheet, no `!important`, nothing that shifts when the app updates its
 *     class names.
 *   - the score, the review count and the button label come from Google rather
 *     than being typed into the theme editor and going stale.
 *   - all of the reviews are available, not the three a widget's free tier
 *     happens to show, and which of them appear is a theme-editor decision.
 *
 * Three things this deliberately does NOT do:
 *
 *   1. It never renders on the server, so the section's own review blocks are
 *      what the page ships with. If the fetch fails, is blocked, or the app is
 *      uninstalled, those stay exactly where they are — the section degrades to
 *      the hand-written cards rather than to an empty hole. Every early return
 *      below is that promise being kept.
 *
 *   2. It never writes review text through `innerHTML`. Google review text is
 *      third-party content; it goes in through `textContent` only. The one
 *      template string that does use `innerHTML` is the star SVG, which
 *      contains no data at all.
 *
 *   3. It does not fetch until the section is near the viewport, and it caches
 *      the response for the session. The payload is ~70KB and the section sits
 *      well down the homepage; paying that on first paint for something nobody
 *      has scrolled to yet would be a poor trade.
 */

const STAR_SVG =
  '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
  '<path fill="currentColor" d="M10 1.6l2.47 5.02 5.53.8-4 3.9.94 5.5L10 14.23l-4.94 2.6.94-5.5-4-3.9 5.53-.8z"/>' +
  '</svg>';

/* Judge.me's review row. The rail drives it exactly as it drives the section's
   own, so both kinds of card scroll and snap the same way. */
const APP_ROW_SELECTOR = '.jdgm-masonry-grid__container';

const CACHE_PREFIX = 'veeze-reviews:';

/* Google hands the timestamp back in microseconds, not the milliseconds Date
   expects — a 16-digit number where JS wants 13. */
const MICROSECONDS_PER_MS = 1000;

class VeezeReviews extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.veeze-reviews__grid');

    /* The rail is set up first: its buttons are needed even when the cards are
       the section's own blocks rather than Google's. */
    if (this.grid) {
      this.setUpRail();
    } else {
      this.waitForAppRow();
    }

    /* Any other mode means the section renders its own blocks and the fetching
       below has nothing to do. */
    if (this.dataset.source !== 'live') return;

    this.endpoint = this.dataset.endpoint;
    if (!this.grid || !this.endpoint) return;

    this.config = {
      max: this.readNumber('max', 3),
      minRating: this.readNumber('minRating', 1),
      requireText: this.dataset.requireText === 'true',
      sort: this.dataset.sort || 'recent',
      exclude: (this.dataset.exclude || '')
        .split(',')
        .map((term) => term.trim().toLowerCase())
        .filter(Boolean),
      stars: this.readNumber('stars', 5),
      quoteMax: this.readNumber('quoteMax', 0),
      quoteMaxMobile: this.readNumber('quoteMaxMobile', 0),
      columns: this.readNumber('columns', 3),
      moreLabel: this.dataset.moreLabel || '',
      lessLabel: this.dataset.lessLabel || '',
      locale: this.dataset.locale || 'de-DE',
      liveScore: this.dataset.liveScore === 'true',
      sublineTemplate: this.dataset.sublineTemplate || '',
      ctaTemplate: this.dataset.ctaTemplate || '',
      cacheMinutes: this.readNumber('cacheMinutes', 30),
    };

    /*
     * `rootMargin` starts the request while the section is still a screen away,
     * so the cards are usually in place before it is scrolled to. Without
     * IntersectionObserver the fetch simply happens now.
     */
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  /*
   * Crossing the breakpoint changes which quote limit applies, so the cards are
   * rebuilt. The payload is already in hand, which is why this is cheap enough
   * to do on rotation rather than leaving a phone in landscape with the
   * portrait truncation.
   */
  watchBreakpoint() {
    this.media = window.matchMedia('(max-width: 749px)');
    this.onBreakpoint = () => {
      if (this.payload) this.load();
    };
    this.media.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.railObserver?.disconnect();
    this.appRowObserver?.disconnect();
    this.media?.removeEventListener('change', this.onBreakpoint);
  }

  /*
   * 180 characters is four lines of the desktop card - 20/32 across 503px - and
   * six of the mobile one, whose quote is 14/19 across 287px and has 76px of
   * room. One figure cannot describe both, so each breakpoint has its own.
   */
  get quoteLimit() {
    const mobile = this.config.quoteMaxMobile;
    if (mobile > 0 && window.matchMedia('(max-width: 749px)').matches) return mobile;
    return this.config.quoteMax;
  }

  /* ---- The rail ------------------------------------------------------
   * Three cards are in view and the rest are a scroll away, so paging moves by
   * whole pages: `clientWidth` is exactly three cards and two gaps, which is
   * what makes a press land on a card edge and the snap agree with it. Reading
   * the width rather than multiplying a card width by three also means the
   * arrows stay correct at the breakpoint where the row becomes narrower,
   * without being told the count.
   */

  /*
   * A review app renders its own row, and it renders it late: the widget is a
   * Vue application that mounts well after this element connects, so the row is
   * not in the DOM to be found at that moment.
   *
   * The observer stops at the first row it sees. It watches this section only,
   * never the document, and disconnects the moment it has what it came for — an
   * observer left running over an app's subtree fires on every re-render it
   * makes.
   */
  waitForAppRow() {
    const attach = () => {
      const row = this.querySelector(APP_ROW_SELECTOR);
      if (!row) return false;
      this.grid = row;
      this.setUpRail();
      return true;
    };

    if (attach()) return;

    this.appRowObserver = new MutationObserver(() => {
      if (attach()) this.appRowObserver.disconnect();
    });
    this.appRowObserver.observe(this, { childList: true, subtree: true });
  }

  setUpRail() {
    this.nav = this.querySelector('[data-rail-nav]');
    if (!this.grid || !this.nav) return;

    this.prev = this.nav.querySelector('[data-rail-prev]');
    this.next = this.nav.querySelector('[data-rail-next]');
    if (!this.prev || !this.next) return;

    this.prev.addEventListener('click', () => this.page(-1));
    this.next.addEventListener('click', () => this.page(1));

    /*
     * `scroll` fires continuously while a smooth scroll animates, so the
     * update is deferred to an animation frame; without that, holding a press
     * queues one recalculation per frame of the animation.
     */
    let frame = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        this.updateRail();
      });
    };

    this.grid.addEventListener('scroll', schedule, { passive: true });

    /* The row's width decides whether there is anything to page through, so a
       resize can turn the buttons on or off. */
    this.railObserver = new ResizeObserver(schedule);
    this.railObserver.observe(this.grid);

    this.updateRail();
  }

  page(direction) {
    /* One card of overlap, so the card at the edge is not cut in half by the
       jump — and never less than a single card's step. */
    const step = Math.max(this.grid.clientWidth * 0.9, 1);
    this.grid.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  updateRail() {
    if (!this.nav || !this.prev) return;

    const overflow = this.grid.scrollWidth - this.grid.clientWidth;

    /*
     * Hidden entirely when everything already fits: two dead buttons under a
     * row of three cards would say there is more to see when there is not.
     * 2px of slack absorbs sub-pixel rounding on a fractional card width -
     * 561.33 does not divide evenly into any viewport.
     */
    this.nav.hidden = overflow <= 2;
    if (this.nav.hidden) return;

    const left = this.grid.scrollLeft;
    this.prev.disabled = left <= 2;
    this.next.disabled = left >= overflow - 2;
  }

  readNumber(key, fallback) {
    const value = Number.parseFloat(this.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  }

  async load() {
    let payload = this.readCache();

    if (!payload) {
      try {
        const response = await fetch(this.endpoint, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        payload = await response.json();
      } catch {
        /* Offline, blocked, app uninstalled, proxy moved — the section keeps
           the cards the server already rendered. */
        return;
      }
      this.writeCache(payload);
    }

    this.payload = payload;
    if (!this.media) this.watchBreakpoint();

    const reviews = this.select(payload?.reviews);
    if (!reviews.length) return;

    this.render(reviews);
    if (this.config.liveScore) this.renderScore(payload);
  }

  /* ---- Cache -----------------------------------------------------------
   * Per session and per endpoint. Reviews change a few times a month, so
   * re-fetching on every page view buys nothing. A malformed or expired entry
   * is treated as a miss rather than an error.
   */

  get cacheKey() {
    return CACHE_PREFIX + this.endpoint;
  }

  readCache() {
    if (this.config.cacheMinutes <= 0) return null;
    try {
      const raw = sessionStorage.getItem(this.cacheKey);
      if (!raw) return null;
      const { at, payload } = JSON.parse(raw);
      const age = (Date.now() - at) / 60000;
      return age < this.config.cacheMinutes ? payload : null;
    } catch {
      return null;
    }
  }

  writeCache(payload) {
    if (this.config.cacheMinutes <= 0) return;
    try {
      sessionStorage.setItem(this.cacheKey, JSON.stringify({ at: Date.now(), payload }));
    } catch {
      /* Private mode, or the quota is full. Not worth reporting. */
    }
  }

  /* ---- Choosing which reviews appear ---------------------------------- */

  select(reviews) {
    if (!Array.isArray(reviews)) return [];

    const chosen = reviews.filter((review) => {
      const rating = Number.parseFloat(review?.rating);
      if (!Number.isFinite(rating) || rating < this.config.minRating) return false;
      if (this.config.requireText && !review?.text?.trim()) return false;

      /*
       * A rating filter cannot catch everything. One review in this listing is
       * five stars whose text opens "Sehr enttauschende Erfahrung" and only
       * turns positive several paragraphs down - so truncated to a card it
       * reads as a complaint while counting as a five. Hence an editorial
       * exclusion list, matched against the text and the author's name.
       */
      if (this.config.exclude.length) {
        const haystack = `${review?.text || ''} ${review?.author_name || ''}`.toLowerCase();
        if (this.config.exclude.some((term) => haystack.includes(term))) return false;
      }

      return true;
    });

    /*
     * A five-star review with no text is a real rating but an empty card, which
     * is why `requireText` exists and defaults on. Of the 100 the proxy
     * returns, 21 are ratings without a word in them.
     */
    if (this.config.sort === 'rating') {
      chosen.sort(
        (a, b) =>
          Number.parseFloat(b.rating) - Number.parseFloat(a.rating) ||
          this.timestamp(b) - this.timestamp(a)
      );
    } else {
      chosen.sort((a, b) => this.timestamp(b) - this.timestamp(a));
    }

    return this.config.max > 0 ? chosen.slice(0, this.config.max) : chosen;
  }

  timestamp(review) {
    const raw = Number.parseFloat(review?.published_date);
    return Number.isFinite(raw) ? raw : 0;
  }

  /* ---- Rendering ------------------------------------------------------ */

  render(reviews) {
    const fragment = document.createDocumentFragment();
    reviews.forEach((review) => fragment.append(this.card(review)));

    this.grid.replaceChildren(fragment);

    /*
     * Columns come from the setting, not from how many reviews arrived. Taking
     * it from the count was right while that count was three; with every review
     * showing it would silently make the row four across, where the design is
     * three. It is still capped by the count, so two reviews give two columns
     * rather than two cards and a hole.
     */
    this.grid.style.setProperty(
      '--veeze-reviews-columns',
      String(Math.max(1, Math.min(reviews.length, this.config.columns)))
    );
    this.dataset.state = 'live';

    /* The rail now holds a different number of cards, so whether it can scroll
       - and how far - has to be recomputed. */
    this.grid.scrollTo({ left: 0 });
    this.updateRail();
  }

  card(review) {
    const card = document.createElement('li');
    card.className = 'veeze-reviews__card';

    const top = document.createElement('div');
    top.className = 'veeze-reviews__card-top';
    top.append(this.stars(review));

    const date = this.date(review);
    if (date) {
      const dateEl = document.createElement('p');
      dateEl.className = 'veeze-reviews__date';
      dateEl.textContent = date;
      top.append(dateEl);
    }
    card.append(top);

    const text = review?.text?.trim();
    if (text) card.append(this.quote(text));

    const foot = document.createElement('div');
    foot.className = 'veeze-reviews__card-foot';
    const author = document.createElement('p');
    author.className = 'veeze-reviews__author';
    author.textContent = review?.author_name || '';
    foot.append(author);
    card.append(foot);

    return card;
  }

  stars(review) {
    const rating = Number.parseFloat(review?.rating) || 0;
    const wrap = document.createElement('div');
    wrap.className = 'veeze-reviews__stars veeze-reviews__stars--card';
    wrap.style.setProperty('--veeze-reviews-star-fill', `${(rating / 5) * 100}%`);
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', `${this.number(rating, 1)} / ${this.config.stars}`);

    ['track', 'fill'].forEach((part) => {
      const span = document.createElement('span');
      span.className = `veeze-reviews__stars-${part}`;
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = STAR_SVG.repeat(this.config.stars);
      wrap.append(span);
    });

    return wrap;
  }

  /*
   * The card is 227px tall around a 20/32 quote, which is about four lines.
   * Google reviews here run to 1769 characters, so a long one has to be cut or
   * it would burst the card — but cutting without a way back would hide what a
   * customer wrote, so the remainder is one click away and expands in place.
   */
  quote(text) {
    const quote = document.createElement('blockquote');
    quote.className = 'veeze-reviews__quote';

    const limit = this.quoteLimit;
    if (!limit || text.length <= limit || !this.config.moreLabel) {
      quote.textContent = text;
      return quote;
    }

    /* Cut at a space so a word is never split down the middle. */
    let cut = text.lastIndexOf(' ', limit);
    if (cut < limit * 0.6) cut = limit;

    const short = document.createElement('span');
    short.textContent = `${text.slice(0, cut).trimEnd()}… `;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'veeze-reviews__more';
    toggle.textContent = this.config.moreLabel;
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';

      /*
       * The card is a fixed 177 on mobile with its overflow hidden, so the
       * stylesheet needs to know when to let it grow — otherwise "Mehr lesen"
       * would reveal text into a clipped box.
       */
      quote.closest('.veeze-reviews__card')?.toggleAttribute('data-expanded', !open);
      short.textContent = open ? `${text.slice(0, cut).trimEnd()}… ` : `${text} `;
      toggle.textContent = open ? this.config.moreLabel : this.config.lessLabel || this.config.moreLabel;
      toggle.setAttribute('aria-expanded', String(!open));
    });

    quote.append(short, toggle);
    return quote;
  }

  date(review) {
    const micro = Number.parseFloat(review?.published_date);
    if (!Number.isFinite(micro)) return review?.readable_date || '';

    /*
     * `readable_date` comes back as English relative text ("3 days ago"), which
     * has no place in a German storefront — so the timestamp is formatted
     * instead, in the locale the section is given.
     */
    try {
      return new Intl.DateTimeFormat(this.config.locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(micro / MICROSECONDS_PER_MS));
    } catch {
      return review?.readable_date || '';
    }
  }

  /* ---- Score, count and button label ---------------------------------- */

  renderScore(payload) {
    const average = Number.parseFloat(payload?.average_rating);
    const total = Number.parseInt(payload?.user_ratings_total, 10);

    if (Number.isFinite(average)) {
      const score = this.querySelector('.veeze-reviews__score');
      if (score) score.textContent = this.number(average, 1);

      /* The header's star row is the same two-layer clip as a card's. */
      const stars = this.querySelector('.veeze-reviews__head > .veeze-reviews__stars');
      if (stars) {
        stars.style.setProperty('--veeze-reviews-star-fill', `${(average / 5) * 100}%`);
        stars.setAttribute('aria-label', `${this.number(average, 1)} / ${this.config.stars}`);
      }
    }

    if (!Number.isFinite(total)) return;

    /*
     * The count appears in the subline and again on the button, so both are
     * given as templates holding `{count}` rather than as finished strings —
     * otherwise the live number could only ever replace one of them.
     */
    const count = this.number(total, 0);
    this.fill('.veeze-reviews__subline', this.config.sublineTemplate, count);
    this.fill('.veeze-reviews__cta-label', this.config.ctaTemplate, count);
  }

  fill(selector, template, count) {
    if (!template) return;
    const node = this.querySelector(selector);
    if (node) node.textContent = template.replace('{count}', count);
  }

  number(value, decimals) {
    try {
      return new Intl.NumberFormat(this.config.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch {
      return String(value);
    }
  }
}

if (!customElements.get('veeze-reviews')) {
  customElements.define('veeze-reviews', VeezeReviews);
}
